import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { employeeSignMyPaf, getMyPafById, resetMyPafRecord } from "actions";
import {
  buildSectionCategoriesFromRecord,
  getManagerCommentsFromRecord,
  getMonthYearLabel,
  getMyPafStatusLabel,
  getOverallScoreLabel,
  getPafNameLabel,
  getSupervisorCommentsFromRecord,
  getSupervisorNameFromRecord,
  mergeMyPafViewRecord,
} from "pages/AppraisalManagment/utils/myPafHelpers";
import Header from "../../Appraisal/AppraisalList/component/Header/Header";
import MyPafScoreSections from "../component/MyPafScoreSections";
import MyPafEmployeeSignPanel from "../component/MyPafEmployeeSignPanel";
import "../../Appraisal/AppraisalProgress.css";

const MyPafView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const appraisalState = useSelector((state) => state.appraisalState);
  const [isSigning, setIsSigning] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const recordId =
    location.state?.recordId || queryParams.get("id") || appraisalState.myPafRecord?.id;

  const record = useMemo(() => {
    const fromStore = appraisalState.myPafRecord;
    const fromState = location.state?.record;
    if (!recordId) return fromStore || fromState || {};
    if (fromStore?.id && fromState?.id && String(fromStore.id) === String(fromState.id)) {
      return mergeMyPafViewRecord(fromState, fromStore);
    }
    return fromStore || fromState || {};
  }, [appraisalState.myPafRecord, location.state?.record, recordId]);

  useEffect(() => {
    if (recordId) {
      dispatch(getMyPafById(recordId));
    }
    return () => {
      dispatch(resetMyPafRecord());
    };
  }, [dispatch, recordId]);

  const sectionCategories = useMemo(
    () => buildSectionCategoriesFromRecord(record),
    [record]
  );

  const overallScoreLabel = useMemo(() => getOverallScoreLabel(record), [record]);

  const supervisorComments = getSupervisorCommentsFromRecord(record);
  const managerComments = getManagerCommentsFromRecord(record);

  const handleSign = async (payload) => {
    if (!recordId) return;
    setIsSigning(true);
    try {
      await dispatch(employeeSignMyPaf(recordId, payload));
      await dispatch(getMyPafById(recordId));
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <>
      <Header title="My PAF Details" />
      <div className="main-content-div">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button type="button" className="c-b-btn" onClick={() => navigate("/my-pafs")}>
          <img className="me-2" src="/assets/images/back-arrow.svg" alt="back" />
          Back
        </button>
        <span className="badge bg-secondary text-capitalize">
          {getMyPafStatusLabel(record)}
        </span>
      </div>

      <div className="appraisal-progress-form mb-3">
        <div className="p-3">
          <Row>
            <Col lg={3}>
              <p className="label-input mb-1">PAF Name</p>
              <p className="font-13 heading-color mb-3">{getPafNameLabel(record)}</p>
            </Col>
            <Col lg={3}>
              <p className="label-input mb-1">Month/Year</p>
              <p className="font-13 heading-color mb-3">{getMonthYearLabel(record) || "—"}</p>
            </Col>
            <Col lg={3}>
              <p className="label-input mb-1">Supervisor Name</p>
              <p className="font-13 heading-color mb-3">
                {getSupervisorNameFromRecord(record) || "—"}
              </p>
            </Col>
            <Col lg={3}>
              <p className="label-input mb-1">Overall Score / Summary</p>
              <p className="font-13 font-weight-s-bold heading-color mb-3">{overallScoreLabel}</p>
            </Col>
          </Row>
        </div>
      </div>

      <div className="appraisal-progress-form mb-3 p-3">
        <h5 className="font-14 font-weight-bold heading-color mb-3">Scoring Details</h5>
        <MyPafScoreSections sectionCategories={sectionCategories} />
      </div>

      <div className="appraisal-progress-form mb-3 p-3">
        <h5 className="font-14 font-weight-bold heading-color mb-2">Supervisor Comments</h5>
        <p className="font-13 text-color mb-0">{supervisorComments || "—"}</p>
      </div>

      {managerComments ? (
        <div className="appraisal-progress-form mb-3 p-3">
          <h5 className="font-14 font-weight-bold heading-color mb-2">Manager Comments</h5>
          <p className="font-13 text-color mb-0">{managerComments}</p>
        </div>
      ) : null}

      <MyPafEmployeeSignPanel
        record={record}
        onConfirm={handleSign}
        onCancel={() => navigate("/my-pafs")}
        isSubmitting={isSigning}
      />
      </div>
    </>
  );
};

export default MyPafView;
